import type { AxiosError } from 'axios';

/**
 * Normalized error type produced by the axios response interceptor.
 *
 * Every rejection bubbles up as an ApiError. UI code can branch on
 * `statusCode` for HTTP-level handling or on `code` for backend-defined
 * error codes (e.g. 'INSUFFICIENT_STOCK', 'INVALID_DISCOUNT').
 */
export class ApiError extends Error {
  readonly statusCode: number;
  readonly code: string | null;
  readonly details: unknown;
  /** True when the request never reached the server (network / abort). */
  readonly isNetworkError: boolean;

  constructor(params: {
    message: string;
    statusCode: number;
    code?: string | null;
    details?: unknown;
    isNetworkError?: boolean;
  }) {
    super(params.message);
    this.name = 'ApiError';
    this.statusCode = params.statusCode;
    this.code = params.code ?? null;
    this.details = params.details;
    this.isNetworkError = params.isNetworkError ?? false;
  }
}

/**
 * Convert any axios rejection into a typed ApiError. Falls back to
 * generic messages so callers never see an axios-specific shape.
 */
export function toApiError(error: AxiosError | Error | unknown): ApiError {
  if (error instanceof ApiError) return error;

  const axiosErr = error as AxiosError;
  // 1. Server responded with an error status.
  if (axiosErr && axiosErr.response) {
    const status = axiosErr.response.status ?? 500;
    const data = axiosErr.response.data as
      | {
          message?: string | string[];
          error?: string;
          code?: string;
          reason?: string;
        }
      | undefined;

    const rawMessage = Array.isArray(data?.message)
      ? data?.message.join(', ')
      : (data?.message as string | undefined);

    const message =
      rawMessage ||
      (data?.error as string | undefined) ||
      (data?.reason as string | undefined) ||
      defaultMessageForStatus(status);

    return new ApiError({
      message,
      statusCode: status,
      code: data?.code ?? null,
      details: data,
    });
  }

  // 2. Network error (no response received).
  if (axiosErr && axiosErr.request) {
    return new ApiError({
      message: 'Network error — please check your connection and try again.',
      statusCode: 0,
      isNetworkError: true,
    });
  }

  // 3. Unknown.
  const fallbackMessage =
    error instanceof Error ? error.message : 'Unexpected error';
  return new ApiError({
    message: fallbackMessage,
    statusCode: 0,
    isNetworkError: false,
  });
}

function defaultMessageForStatus(status: number): string {
  switch (status) {
    case 400:
      return 'Invalid request';
    case 401:
      return 'Authentication required';
    case 403:
      return 'Access denied';
    case 404:
      return 'Resource not found';
    case 409:
      return 'Conflict — please refresh and try again';
    case 422:
      return 'Invalid input';
    case 429:
      return 'Too many requests — please wait a moment';
    case 500:
      return 'Server error — please try again';
    case 502:
    case 503:
    case 504:
      return 'Service temporarily unavailable';
    default:
      return `Request failed (${status})`;
  }
}
